import { Link, useParams } from "react-router-dom";
import AuthLayout from "../../../../components/layouts/auth"
import { useSingleBusinessTransaction } from "../../../../composables/business-merchant/useSingleBusinessTransaction";
import LineLoader from "../../../../components/loader/line-loader";
import { ChevronLeft, Minus, Plus } from "lucide-react";
import SinglePageTitle from "../../../../components/ui/SinglePageTitle";
import Naira from "../../../../components/naira";
import { numberWithCommas } from "../../../../lib/helper-support";
import TransactionStatusBadge from "../../../../components/ui/transaction-status-badge";
import moment from "moment";

const SingleBusinessTransaction = () => {
    const { reference } = useParams<{ reference: string }>();

    const { data: transaction, isLoading: pageLoading } = useSingleBusinessTransaction({
        reference: reference || '',
        enabled: !!reference,
    })

    return (
        <AuthLayout>
            <div>
                {pageLoading && <LineLoader />}
                {(!transaction?.reference && !pageLoading) && (
                    <div>
                        <h1 className="text-[2em] font-[600] text-primary">Transaction Not Found</h1>
                        <Link to={'/business-transactions'} className="flex items-center mt-5 space-x-2 font-[400] text-gray-500 hover:underline">
                            <ChevronLeft size={13} />
                            <span>back to transactions</span>
                        </Link>
                    </div>
                )}
                {(!pageLoading && transaction.reference) && (
                    <div>
                        <h2 className="text-[#8D8D8D] font-[600] text-[14px]">Transactions / details</h2>
                        <div className="mt-4 lg:gap-5 lg:flex">
                            <div className="lg:w-3/5">
                                <SinglePageTitle
                                    title={`Merchant transaction details: ${reference}`}
                                    link={'/business-transactions'}
                                />
                                <div className="grid gap-y-10 mt-5">
                                    <div className="flex items-center justify-between">
                                        <div className="font-[600] text-tertiary">Settled Amount</div>
                                        <div className="flex items-center text-primary">
                                            {transaction.direction === 'credit' && <Plus size={18} strokeWidth={3} />}
                                            {transaction.direction === 'debit' && <Minus size={18} strokeWidth={3} />}
                                            <h2 className="text-[2em] font-[600]">
                                                {<Naira />}{numberWithCommas(transaction.settled_amount)}.<span className="text-[18px]">{numberWithCommas(transaction.settled_amount, true, true)}</span>
                                            </h2>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="font-[600] text-tertiary">Amount</div>
                                        <div className="flex items-center">
                                            <h2 className="text-[18px] font-[600] text-tertiary">
                                                {<Naira />}{numberWithCommas(transaction.amount)}.<span>{numberWithCommas(transaction.amount, true, true)}</span>
                                            </h2>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="font-[600] text-tertiary">Processing Fee</div>
                                        <div className="flex items-center">
                                            <h2 className="text-[18px] font-[600] text-tertiary">
                                                {<Naira />}{numberWithCommas(transaction.charge_amount)}.<span>{numberWithCommas(transaction.charge_amount, true, true)}</span>
                                            </h2>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="font-[600] text-tertiary">To</div>
                                        <div className="text-tertiary text-[18px] text-right font-[600]">
                                            <span>{transaction.customer.display_name}</span>
                                            {transaction.customer.email && <span className="block text-xs font-medium text-[#656565]">{transaction.customer.email}</span>}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="font-[600] text-tertiary">Status</div>
                                        <TransactionStatusBadge status={transaction.status} />
                                    </div>
                                </div>
                                <table className="w-full mt-16 font-[500]">
                                    <tbody>
                                        <tr className="border-b border-[#C4C4C4]">
                                            <td className="py-3 text-tertiary">Infrastructure</td>
                                            <td className="py-3 pl-6 text-[#656565]">{transaction.provider ?? 'Cashir'}</td>
                                        </tr>
                                        <tr className="border-b border-[#C4C4C4]">
                                            <td className="py-3 text-tertiary">Transaction date</td>
                                            <td className="py-3 pl-6 text-[#656565]">{moment(transaction.created_at).format('MMM Do, YYYY h:mm a')}</td>
                                        </tr>
                                        <tr className="border-b border-[#C4C4C4]">
                                            <td className="py-3 text-tertiary">Reference ID</td>
                                            <td className="py-3 pl-6 text-[#656565]">{transaction.reference}</td>
                                        </tr>
                                        <tr className="border-b border-[#C4C4C4]">
                                            <td className="py-3 text-tertiary">Narrative</td>
                                            <td className="py-3 pl-6 text-[#656565]">{transaction.narration ?? transaction.remarks ?? <Minus />}</td>
                                        </tr>
                                        <tr className="border-b border-[#C4C4C4]">
                                            <td className="py-3 text-tertiary">Transaction type</td>
                                            <td className="py-3 pl-6">
                                                <div className="font-[600] uppercase bg-[#F8FAFB] py-2 px-5 rounded-full text-tertiary border border-primary w-fit text-[13px]">
                                                    {transaction.type}
                                                </div>
                                            </td>
                                        </tr>
                                        {/* <tr>
                                        <td className="py-3 text-tertiary">Infrastructure type</td>
                                        <td className="py-3 pl-6">
                                            <div className="font-[600] bg-[#2DB744] py-2 px-5 rounded-full text-white w-fit text-[13px]">
                                                Anchor bank
                                            </div>
                                        </td>
                                    </tr> */}
                                    </tbody>
                                </table>
                            </div>
                            <div className="mt-10 lg:pl-5 lg:w-2/5 lg:mt-0">
                                <div className="border border-[#8D8D8D] rounded-[30px] py-5 min-h-[15em] lg:min-h-[35em]">
                                    <div className="px-10 text-tertiary font-[600]">Cash Balance History</div>
                                    <div className="mt-5 overflow-x-auto">
                                        <table className="w-full">
                                            <tbody>
                                                <tr className="text-tertiary font-[600] text-[14px] bg-[#D9D9D980]">
                                                    <td className="py-2 pl-10">Before</td>
                                                    <td className="py-2 pl-10">After</td>
                                                    <td className="px-5 py-2"></td>
                                                </tr>
                                                {transaction.balance_history?.map((transaction: any, index: number) => (
                                                    <tr key={index} className="border-b border-[C4C4C4] text-[14px] font-[600] text-[#656565]">
                                                        <td className="py-5 pl-10">{<Naira />}{numberWithCommas(transaction.balance_before ?? 0)}</td>
                                                        <td className="py-5 pl-10">{<Naira />}{numberWithCommas(transaction.balance_after ?? 0)}</td>
                                                        <td className="px-5 py-5">
                                                            <div style={{ color: transaction.type === 'credit' ? '#009C2C' : '#FE4D97' }} className="capitalize flex items-center px-5 font-[600] bg-[#F3F3F3] justify-center p-2 rounded-full text-[11px]">
                                                                <span>{transaction.type}</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AuthLayout>
    )
}

export default SingleBusinessTransaction